import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import ErrorBoundary from './presentation/components/common/ErrorBoundary';
import AuthRepository from './domain/repositories/AuthRepository';
import apiClient from './api/apiClient';
import './App.css';

// Import components
import Navbar from './presentation/components/Navbar';
import Footer from './presentation/components/common/Footer';
import Home from './presentation/components/Home';
import LoginForm from './presentation/components/LoginForm';
import OneOffCertificate from './presentation/components/OneOffCertificate';
import CsvUploader from './presentation/components/CsvUploader';
import Table from './presentation/components/Table';
import AddCompetitor from './presentation/components/AddCompetitor';
import AddSection from './presentation/components/AddSection';
import Competition from './presentation/components/Competition';
import CompetitorDetails from './presentation/components/CompetitorDetails';
import UpdateCompetitor from './presentation/components/UpdateCompetitor';
import CompetitionsPage from './presentation/components/CompetitionsPage';

export const AuthContext = createContext();

function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const authRepository = new AuthRepository();
  const navigate = useNavigate();

  const checkIsAuthenticated = async () => {
    try {
      const isAuth = await authRepository.isAuthenticated();
      setIsAuthenticated(isAuth);
    } catch (error) {
      console.error('Initial authentication check failed:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkIsAuthenticated();
  }, []);

  const login = () => {
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      await authRepository.logout();
      setIsAuthenticated(false);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    apiClient.setLogoutCallback(logout);
  }, [logout]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function PrivateRoute({ element: Element }) {
  const { isAuthenticated } = useContext(AuthContext);
  const location = useLocation();
  return isAuthenticated ? (
    <Element />
  ) : (
    <Navigate to="/login" state={{ from: location }} replace />
  );
}

function App() {
  const location = useLocation();
  const { isAuthenticated, logout } = useContext(AuthContext);

  return (
    <div className="App">
      {location.pathname !== '/login' && (
        <Navbar isAuthenticated={isAuthenticated} onLogout={logout} />
      )}
      <ErrorBoundary>
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <LoginForm />
              )
            }
          />
          <Route path="/" element={<PrivateRoute element={Home} />} />
          <Route path="/oneoff" element={<PrivateRoute element={OneOffCertificate} />} />
          <Route path="/upload/csv" element={<PrivateRoute element={CsvUploader} />} />
          <Route path="/competitions" element={<PrivateRoute element={CompetitionsPage} />} />
          <Route path="/competition/:id" element={<PrivateRoute element={Competition} />} />
          <Route
            path="/competitor/:id"
            element={<PrivateRoute element={CompetitorDetails} />}
          />
          <Route
            path="/competitor/:id/edit"
            element={<PrivateRoute element={UpdateCompetitor} />}
          />
          <Route path="/add-competitor" element={<PrivateRoute element={AddCompetitor} />} />
          <Route path="/add-section" element={<PrivateRoute element={AddSection} />} />
          <Route path="/table" element={<PrivateRoute element={Table} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
      {location.pathname !== '/login' && <Footer />}
    </div>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <AuthProvider>
        <App />
      </AuthProvider>
    </Router>
  );
}